import { useMemo } from 'react'
import { HOMESTEAD_SERIES_EPISODE_1_ID } from '@/constants/episode-ids'
import { useRegionContext } from '@/contexts/RegionContext'
import { Banner, BannerViewModel } from '@/molecules/Banner'
import { HorizontalBannerScroller } from '@/molecules/HorizontalBannerScroller'
import { useGuildUser } from '@/services/GuildUserService'
import { useGetWatchableEpisodeByGuid } from '@/services/ProjectsService/ProjectsService'
import { formatBannerViewModel, useGetBannerGroupByIdQuery } from '@/services/UltrawideBannerService'
import { useUser } from '@/services/UserService'
import { UltrawideBanner } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { isDefined } from '@/utils/types'
import { BannerCarouselPlaceholder } from '@/views/WatchLandingView/BannerCarousel/BannerCarouselPlaceholder'

export interface BannerCarouselProps {
  groupId: string
  preview?: boolean
}

const initialPifBanner = {
  backgroundCloudinaryPath: '/v1734642073/angel-studios/watch-banners/4800x2057_Ultrawide_Banner_v1.webp',
  backgroundStyle: {},
  descriptionText: '',
  gravity: null,
  href: '/watch/homestead-series/episode/dee2b65a-ccaa-437b-810d-bbd36bcfd98f/season-1/episode-1/the-wolves-circle',
  id: 'initial-pif-banner',
  logoStyle: {},
  mobileBackgroundCloudinaryPath: '/v1734642072/angel-studios/watch-banners/374x200_v1.webp',
  tabletBackgroundCloudinaryPath: '/v1734642073/angel-studios/watch-banners/703x326_Tablet_v1.webp',
  titleImage: undefined,
}

const finishedPifBanner = {
  backgroundCloudinaryPath: '/v1734642182/angel-studios/watch-banners/4800x2057_Ultrawide_Banner_v2.webp',
  backgroundStyle: {},
  descriptionText: '',
  gravity: null,
  href: '/m/offer/winter24?utm_campaign=homestead-film-to-series&utm_medium=banner&utm_source=thank-you',
  id: 'finished-pif-banner',
  logoStyle: {},
  mobileBackgroundCloudinaryPath: '/v1734642181/angel-studios/watch-banners/374x200_v2.webp',
  tabletBackgroundCloudinaryPath: '/v1734642180/angel-studios/watch-banners/703x326_Tablet_v2.webp',
  titleImage: undefined,
}

export const BannerCarousel: React.FC<BannerCarouselProps> = ({ groupId, preview = false }) => {
  const { loading, banners: bannersToShow } = useGetBannersByGroupId(groupId, preview)
  const { isLoggedIn } = useUser()
  const isGuildMember = useGuildUser()

  const homesteadGuid = HOMESTEAD_SERIES_EPISODE_1_ID
  const { watchableEpisode } = useGetWatchableEpisodeByGuid(homesteadGuid, isLoggedIn)
  const hasPifAvailability = Boolean(
    watchableEpisode?.watchableReasons?.some((reason) => reason?.__typename === 'PifContentAvailabilityAction'),
  )
  const hasWatchedFirstEpisode = Boolean(
    watchableEpisode?.watchPosition?.position && watchableEpisode.watchPosition.position >= 2700,
  )

  if (loading)
    return (
      <div data-testid="banners-placeholder">
        <BannerCarouselPlaceholder />
      </div>
    )

  const banners = [...bannersToShow]
  if (hasPifAvailability) {
    if (hasWatchedFirstEpisode) {
      if (!isGuildMember) {
        banners.unshift(finishedPifBanner)
      }
    } else {
      banners.unshift(initialPifBanner)
    }
  }

  return (
    <HorizontalBannerScroller>
      {banners?.map((banner) => {
        return banner && <Banner key={banner.id} banner={banner} />
      })}
    </HorizontalBannerScroller>
  )
}

export function useGetBannersByGroupId(
  groupId: string,
  preview: boolean = false,
): { loading: boolean; banners: BannerViewModel[] } {
  const guildUser = useGuildUser()

  const { locale } = useLocale()
  const { region, loading: isRegionLoading } = useRegionContext()

  const { isBannersLoading, bannerData } = useGetBannerGroupByIdQuery({
    id: groupId,
    locale,
    preview,
    region,
    skip: guildUser.loading || isRegionLoading,
  })

  const isGuildMember = guildUser?.isGuildMember

  return useMemo(() => {
    const banners: UltrawideBanner[] = bannerData?.filter(isDefined) || []

    const filteredBanners = filterBanners(banners, isGuildMember)

    return {
      loading: isBannersLoading || guildUser.loading,
      banners: filteredBanners.filter(isDefined).map((banner) => {
        return formatBannerViewModel(banner as UltrawideBanner, isGuildMember)
      }),
    }
  }, [bannerData, guildUser, isBannersLoading, isGuildMember])

  function filterBanners(banners: UltrawideBanner[], isGuildMember: boolean) {
    return isGuildMember ? banners.filter(hasGuildCta) : banners.filter(hasNonGuildCta)

    function hasNonGuildCta(b: UltrawideBanner) {
      return (b?.href?.length || 0) > 0
    }

    function hasGuildCta(b: UltrawideBanner) {
      return (b?.guildHref?.length || 0) > 0
    }
  }
}
